import { HttpErrorResponse } from '@angular/common/http';
import { defer, Observable, throwError } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';

/** Dónde se recuerdan los 403 `trial_locked` de una empresa (`DashboardReadsService`). */
export interface TrialLockedMemory {
  lockedError(corporationId: string, key: string): HttpErrorResponse | undefined;
  rememberLocked(corporationId: string, key: string, error: HttpErrorResponse): void;
}

export interface SharedRequestOptions {
  /** Guardar la respuesta hasta `clear()`/`delete()` (si no, solo se comparte mientras está en curso). */
  keep?: boolean;
  /** Empresa de la lectura: un 403 `trial_locked` se recuerda para ella y no se vuelve a pedir. */
  corporationId?: string;
  /** No vale una respuesta guardada; sí una lectura aún en curso (salió después de la última escritura). */
  fresh?: boolean;
}

export function isTrialLocked(error: unknown): error is HttpErrorResponse {
  return error instanceof HttpErrorResponse && error.status === 403 && error.error?.error_type === 'trial_locked';
}

/**
 * Una lectura que piden varias piezas a la vez (el layout y la página, o dos
 * piezas del layout) sale una sola vez: quien la pide mientras está en curso
 * recibe la misma respuesta. Con `keep`, la respuesta se guarda hasta
 * `clear()`/`delete()`. Un error no se guarda, salvo un 403 `trial_locked`
 * de una empresa, que se recuerda en `locks` hasta que se desbloquea. Si todos
 * dejan de escuchar antes de que llegue, se cancela y se olvida.
 */
export class SharedRequests<T> {
  private readonly entries = new Map<string, Observable<T>>();
  /** Lecturas guardadas que ya respondieron. */
  private readonly done = new Set<Observable<T>>();

  constructor(
    private readonly name = '',
    private readonly locks?: TrialLockedMemory,
  ) {}

  get(key: string, request: () => Observable<T>, options: SharedRequestOptions | boolean = false): Observable<T> {
    const { keep = false, corporationId, fresh = false }: SharedRequestOptions =
      typeof options === 'boolean' ? { keep: options } : options;
    const lockKey = `${this.name}|${key}`;
    const lockedError = corporationId ? this.locks?.lockedError(corporationId, lockKey) : undefined;
    if (lockedError) return throwError(() => lockedError);
    const existing = this.entries.get(key);
    if (existing && !(fresh && this.done.has(existing))) return existing;
    if (existing) this.done.delete(existing);
    let completed = false;
    const shared = defer(request).pipe(
      tap({
        complete: () => {
          completed = true;
          this.done.add(shared);
        },
        error: (error) => {
          if (corporationId && isTrialLocked(error)) this.locks?.rememberLocked(corporationId, lockKey, error);
        },
      }),
      finalize(() => {
        if ((!keep || !completed) && this.entries.get(key) === shared) this.delete(key);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.entries.set(key, shared);
    return shared;
  }

  delete(key: string): void {
    const entry = this.entries.get(key);
    if (entry) this.done.delete(entry);
    this.entries.delete(key);
  }

  clear(): void {
    this.entries.clear();
    this.done.clear();
  }
}
